import type { JsonObject, JsonValue } from '@backstage/types';

/**
 * True for a plain JSON object (not `null`, not an array, not `undefined`). Accepts
 * `undefined` so it can narrow the result of a `noUncheckedIndexedAccess` lookup.
 */
export function isJsonObject(
  value: JsonValue | undefined,
): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Deep-merge two JSON objects. Later keys win. Nested objects merge recursively; a
 * `null` overlay value is written verbatim (it does NOT keep the old value); arrays
 * and primitives are replaced by the overlay.
 */
export function deepMerge(base: JsonObject, overlay: JsonObject): JsonObject {
  const result: JsonObject = { ...base };
  for (const [key, overlayValue] of Object.entries(overlay)) {
    const baseValue = result[key];
    result[key] =
      isJsonObject(baseValue) && isJsonObject(overlayValue)
        ? deepMerge(baseValue, overlayValue)
        : overlayValue;
  }
  return result;
}
